import { useState } from 'react';
import { Edit, DeleteOutline } from '@mui/icons-material';

import PromptForm from './PromptForm';
import { usePromptLibrary } from './usePromptLibrary';
import { Table, Button, Modal, ConfirmModal, SearchPanel } from '../../components';
import type { SystemPrompt } from '../../api';

const PromptLibraryPage = () => {
  const {
    prompts,
    loading,
    search,
    setSearch,
    handleDeletePrompt: deletePrompt,
    isOpenedDeleteModal,
    onCloseDeleteModal,
    onOpenDeleteModal,
    setAgentsSort,
    isOpened,
    onClose,
    onOpen,
    selectedPrompt,
    setSelectedPrompt,
    sortPrompts,
    handleCreatePrompt,
    handleUpdatePrompt,
    loadingPropmpt,
  } = usePromptLibrary();

  const [deletePromptId, setDeletePromptId] = useState<string | null>(null);

  const handleEditPrompt = (prompt: SystemPrompt) => {
    setSelectedPrompt(prompt);
    onOpen();
  };

  const handleOpenDeleteModal = (id: string) => {
    setDeletePromptId(id);
    onOpenDeleteModal();
  };

  const handleConfirmDelete = () => {
    if (deletePromptId) {
      deletePrompt(deletePromptId);
      setDeletePromptId(null);
    }
  };

  const handleCloseModal = () => {
    setSelectedPrompt(null);
    onClose();
  };
  const handleCloseDeleteModal = () => {
    setDeletePromptId(null);
    onCloseDeleteModal();
  };

  const handleSavePrompt = (data: SystemPrompt) => {
    const { title, identifier, prompt } = data;
    if (!title || !identifier || !prompt) {
      return;
    }
    if (selectedPrompt && selectedPrompt.id) {
      const payload = {
        id: selectedPrompt.id,
        title,
        prompt,
      };
      handleUpdatePrompt(payload);
    } else {
      handleCreatePrompt({ title, identifier, prompt });
    }
  };

  return (
    <>
      <ConfirmModal
        title="Delete prompt"
        open={isOpenedDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        confirmButtonLabel="Delete"
      >
        <p className="text-medium">
          Are you sure you want to delete this prompt? This action cannot be
          undone.
        </p>
      </ConfirmModal>

      <Modal
        size="big"
        open={isOpened}
        onClose={handleCloseModal}
        title={selectedPrompt ? 'Edit Prompt' : 'Add Prompt'}
      >
        <PromptForm
          saving={loadingPropmpt}
          onCancel={handleCloseModal}
          onSave={handleSavePrompt}
          initialValue={selectedPrompt || undefined}
          buttonLabel={selectedPrompt ? 'Update' : 'Add'}
        />
      </Modal>

      <div className="flex justify-between items-center mb-sm">
        <SearchPanel
          search={search}
          onSearch={setSearch}
          withPaginate={false}
          searchClassName="text-medium"
        />
        <Button
          label="Add Prompt"
          size="medium"
          onClick={() => {
            setSelectedPrompt(null);
            onOpen();
          }}
        />
      </div>
      <Table
        sort={sortPrompts}
        onSort={setAgentsSort}
        loading={loading}
        data={prompts ?? []}
        verticalAlign="top"
        emptyMessage="No Prompts"
        config={{
          columns: [
            {
              header: 'Title',
              sortName: 'title',
              cellClassName:
                'font-semibold min-w-[14rem] max-w-[16rem] text-mainText content-center',
              getValue: ({ title }) => <div className="truncate">{title}</div>,
            },
            {
              header: 'Identifier',
              cellClassName: 'min-w-[20rem] max-w-[25rem]',
              getValue: ({ identifier }) => {
                return <div className="text-mainText">{identifier}</div>;
              },
            },
            {
              header: '',
              cellClassName:
                'min-w-[8rem] align-end content-center',
              getValue: row => (
                <div className="flex items-center justify-end gap-2">
                  <Button
                    size="small"
                    type="contrast"
                    className="px-2"
                    icon={<Edit className="h-8 w-8 text-buttonPrimary" />}
                    onClick={() => handleEditPrompt(row as SystemPrompt)}
                  />
                  <Button
                    size="small"
                    type="contrast"
                    className="px-2"
                    icon={
                      <DeleteOutline className="h-8 w-8 text-buttonPrimary" />
                    }
                    onClick={() => handleOpenDeleteModal(row.id)}
                  />
                </div>
              ),
            },
          ],
        }}
      />
    </>
  );
};

export default PromptLibraryPage;
